import { useEffect } from "react";
import PublicHeader, { PublicFooter } from "@/components/PublicHeader";
import { Link } from "react-router-dom";
import { MapPin, Users, MessageCircle, ShieldCheck, Lock, Heart } from "lucide-react";

export default function About() {
  useEffect(() => {
    document.title = "About MyLiveFriend — real neighbours, real connections";
  }, []);
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">About MyLiveFriend</h1>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
          MyLiveFriend is a hyperlocal network for India. We help you find and chat with real neighbours — people who actually live in your pincode, area, or city — without the noise of global social apps.
        </p>

        <section className="mt-10">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Why we built this</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">
            People are more connected online than ever but less connected to the folks living three doors down. Whether you want someone to grab chai with, a run buddy, a tenant for your spare room, or just a friend in a new city — MyLiveFriend puts your pincode first.
          </p>
        </section>

        <section className="mt-10 grid sm:grid-cols-2 gap-6">
          {[
            { Icon: MapPin, title: "Pincode-first discovery", body: "Filter neighbours by exact 6-digit pincode, area name, or city — no accidental matches 400 km away." },
            { Icon: Lock, title: "Privacy by design", body: "Profiles are gated behind a subscription so bots and scrapers can't harvest contacts. You stay in control." },
            { Icon: MessageCircle, title: "Real-time chat", body: "WebSocket chat with typing indicators, read-time stamps and browser push — nothing lags behind." },
            { Icon: ShieldCheck, title: "Verified payments", body: "Pay via UPI QR, PhonePe, or NEFT. Admin verifies every manual payment so there are no scams." },
            { Icon: Users, title: "Two clear roles", body: "Pick what you're looking for: Available for Rental or Looking to Rent. Filters match the right people." },
            { Icon: Heart, title: "Built for India", body: "₹ pricing, UPI first, Hindi + English friendly, and 10+ cities seeded with real-looking data to try." },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-2xl border border-slate-200 p-5">
              <f.Icon className="w-7 h-7 text-teal-600" />
              <h3 className="font-heading font-bold text-lg text-slate-900 mt-3">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-2xl bg-gradient-to-br from-teal-50 to-amber-50 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Ready to meet your pincode?</h2>
          <p className="text-slate-600 mt-2">Sign in with Google — no passwords, no spam.</p>
          <Link to="/" className="inline-block mt-4 bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 py-3 font-semibold">
            Get started
          </Link>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
